import React, { useCallback, useEffect, useMemo, useState } from 'react'
import Text from 'core/elements/Text'
import ModalForm from 'core/elements/modal/ModalForm'
import {
  getVmsForHost,
  deauthorizeHypervisorRole,
  deauthorizeImageLibraryRole,
  deauthorizePersistentStorageRole,
  removeHostRole,
  removeDns,
  getBlueprints,
} from '../actions'
import { resmgrHostsSelector } from '../selectors'
import { listImages } from 'openstack/components/images/actions'
import { imagesSelector } from 'openstack/components/images/selectors'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import { useAppSelector } from 'app/store'
import { listAllVolumes } from 'openstack/components/storage/volumes/actions'
import { allVolumesSelector } from 'openstack/components/storage/volumes/selectors'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'

const useStyles = makeStyles<Theme>((theme) => ({
  container: {
    display: 'grid',
    gap: 12,
  },
}))

const isLastImageLibrary = ({ allHosts, host }) => {
  if (!host?.roles?.includes('image-library')) {
    return false
  }
  const imageLibraries = allHosts?.filter((host) => host?.roles?.includes('image-library'))
  return imageLibraries.length === 1 && imageLibraries[0]?.id === host?.id
}

export default function DeauthorizeRolesDialog({ rows: [host], onClose }) {
  let loadingVolumes
  const classes = useStyles()
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [hasVms, setHasVms] = useState(false)
  const [blueprintSettings, setBlueprintSettings] = useState({
    imageLibrarySharedStorage: false,
  })

  const { loading: loadingImages } = useListAction(listImages)
  const imagesList = useAppSelector(imagesSelector)
  if (host?.roles?.includes('persistent-storage')) {
    const { loading: _loading } = useListAction(listAllVolumes)
    loadingVolumes = _loading
  } else {
    loadingVolumes = false
  }
  const volumesList = useAppSelector(allVolumesSelector)

  const allHosts = useSelector(resmgrHostsSelector)

  useEffect(() => {
    const makeCalls = async () => {
      const blueprints = await getBlueprints()
      const blueprint = blueprints?.[0]
      setBlueprintSettings(blueprint)
      if (host?.roles?.includes('hypervisor')) {
        const vms = await getVmsForHost(host?.id)
        setHasVms(!!vms?.length)
      }
      setLoading(false)
    }
    setLoading(true)
    makeCalls()
  }, [host])

  const hostRoles = useMemo(() => {
    const v2Hypervisor = host?.roles?.includes('hypervisor')
    const v2ImageLibrary = host?.roles?.includes('image-library')
    const v2PersistentStorage = host?.roles?.includes('persistent-storage')
    const v2Dns = host?.roles?.includes('dns')
    return {
      v2: {
        hypervisor: v2Hypervisor,
        imageLibrary: v2ImageLibrary,
        persistentStorage: v2PersistentStorage,
        dns: v2Dns,
      },
      v1: host?.v1Roles?.filter((role) => {
        // v1 roles blacklist controlled by v2
        if (
          [
            'pf9-ostackhost-neutron',
            'pf9-neutron-base',
            'pf9-neutron-ovn-controller',
            'pf9-neutron-ovn-metadata-agent',
            'pf9-ceilometer',
            'pf9-glance-role',
            'pf9-cindervolume-base',
            'pf9-cindervolume-config',
            'pf9-designate',
            'pf9-ha-slave',
          ].includes(role)
        ) {
          return false
        }
        return true
      }),
    }
  }, [host])

  const lastImageLibrary = isLastImageLibrary({ allHosts, host })

  const disallowHypervisorRoleDeauth = hasVms
  // Disallow if shared storage not enabled & images exist, otherwise allow it
  const disallowImageLibraryDeauth = blueprintSettings?.imageLibrarySharedStorage
    ? lastImageLibrary && !!imagesList?.length
    : !!imagesList?.length
  const disallowPersistentStorageDeauth = useMemo(() => {
    const hasVolume = volumesList?.some((volume) =>
      volume['os-vol-host-attr:host']?.includes(host?.id),
    )
    return hasVolume
  }, [volumesList])

  const handleSubmit = useCallback(async () => {
    setSubmitting(true)
    if (hostRoles?.v2?.hypervisor && !disallowHypervisorRoleDeauth) {
      await deauthorizeHypervisorRole({ host })
    }
    if (hostRoles?.v2?.imageLibrary && !disallowImageLibraryDeauth) {
      await deauthorizeImageLibraryRole({ host })
    }
    if (hostRoles?.v2?.persistentStorage && !disallowPersistentStorageDeauth) {
      await deauthorizePersistentStorageRole({ host })
    }
    if (hostRoles?.v2?.dns) {
      await removeDns({ hostId: host?.id })
    }
    if (hostRoles?.v1?.length) {
      await Promise.allSettled(
        hostRoles?.v1?.map((role) => {
          return removeHostRole({ hostId: host?.id, role })
        }),
      )
    }
    handleClose()
  }, [
    host,
    disallowHypervisorRoleDeauth,
    disallowImageLibraryDeauth,
    disallowPersistentStorageDeauth,
    hostRoles,
  ])

  const handleClose = () => {
    setSubmitting(false)
    onClose(true)
  }

  return (
    <ModalForm
      title="Remove All Roles"
      onSubmit={handleSubmit}
      onClose={onClose}
      loading={loading || loadingImages || loadingVolumes}
      submitting={submitting}
      // error={error}
      submitTitle={`Confirm`}
      panel="dialog"
      open
    >
      <div className={classes.container}>
        <Text variant="body2">
          Remove all assigned roles and associated packages currently eligible for removal on host{' '}
          <b>{host?.name}</b>.
        </Text>
        {disallowHypervisorRoleDeauth && hostRoles?.v2?.hypervisor && (
          <Text variant="caption1">
            <FontAwesomeIcon>circle-exclamation</FontAwesomeIcon>
            {'  '}VMs are present on this host, so hypervisor related packages will not be removed.
          </Text>
        )}
        {disallowImageLibraryDeauth && hostRoles?.v2?.imageLibrary && (
          <Text variant="caption1">
            <FontAwesomeIcon>circle-exclamation</FontAwesomeIcon>
            {'  '}
            {blueprintSettings.imageLibrarySharedStorage
              ? 'Image Library role will not be removed because it is the last remaining image library and there are still images present on the host.'
              : 'Image Library role will not be removed because there are still images present on the host.'}
          </Text>
        )}
        {disallowPersistentStorageDeauth && hostRoles?.v2?.persistentStorage && (
          <Text variant="caption1">
            <FontAwesomeIcon>circle-exclamation</FontAwesomeIcon>
            {'  '}Volumes are present on this host, so persistent storage related packages will not
            be removed.
          </Text>
        )}
      </div>
    </ModalForm>
  )
}
